// src/components/AgentInventory/AgentInventoryView.jsx
// Enterprise Agent Inventory — cross-provider agent registry with traces, debug, history, audit
import React, { useState, useEffect, useRef } from 'react';
import {
  Bot, Search, RefreshCw, CheckCircle, AlertTriangle, XCircle,
  Clock, Zap, DollarSign, Activity, Shield, Settings, History,
  Terminal, FileText, GitBranch, Eye, Layers, Database, Globe,
  ChevronRight, ChevronDown, Copy, Play, Pause, BarChart2,
  TrendingUp, TrendingDown, Key, Lock, Tag, MessageSquare,
  Server, Hash, Users, Star, Cpu, Network, ArrowRight, Filter,
} from 'lucide-react';

// ─── Provider registry ────────────────────────────────────────────────────────
const PROVIDERS = {
  openai:     { label:'OpenAI',        color:'#10a37f', bg:'#10a37f12', icon:'🤖' },
  anthropic:  { label:'Anthropic',     color:'#d97706', bg:'#d9770612', icon:'🧠' },
  bedrock:    { label:'AWS Bedrock',   color:'#f97316', bg:'#f9731612', icon:'☁️' },
  azure:      { label:'Azure AI',      color:'#0078d4', bg:'#0078d412', icon:'🔷' },
  vertex:     { label:'Google Vertex', color:'#4285f4', bg:'#4285f412', icon:'✨' },
  selfhosted: { label:'Self-Hosted',   color:'#8b5cf6', bg:'#8b5cf612', icon:'🦙' },
};

const ENV_COLOR = { production:'#22c55e', staging:'#f59e0b', development:'#60a5fa' };
const STATUS_CFG = {
  active:     { color:'#22c55e', label:'Active',    dot:true  },
  degraded:   { color:'#f59e0b', label:'Degraded',  dot:true  },
  error:      { color:'#ef4444', label:'Error',     dot:true  },
  inactive:   { color:'#64748b', label:'Inactive',  dot:false },
  deprecated: { color:'#94a3b8', label:'Deprecated',dot:false },
};

// ─── Agent inventory data ─────────────────────────────────────────────────────
const INVENTORY = [
  {
    id:'agt-cs-001', name:'Customer Support Agent', provider:'openai',
    model:'gpt-4o', assistantId:'asst_Kx8mPqR3vTlN2wZ9',
    status:'active', env:'production', version:'v2.4.1',
    description:'Tier-1 support: order tracking, refund processing, FAQ resolution',
    tools:['file_search','code_interpreter','web_search'],
    knowledgeBases:['product-docs-v3','faq-kb','refund-policy'],
    compliance:['SOC2','GDPR'],
    owner:'Platform Team', costCenter:'CS-ENG',
    temperature:0.3, maxTokens:4096, contextWindow:128000,
    requestsToday:1842, requestsMonth:48200, errRate:0.8,
    avgLatencyMs:1240, p95LatencyMs:3400,
    costToday:18.42, costMonth:284.50, tokensMillion:12.4,
    healthScore:98.2, slaTarget:99.5, slaActual:99.7,
    lastActive:'2m ago', createdAt:'2024-11-15', lastDeployed:'3 days ago',
    tags:['customer-facing','tier-1','auto-resolve'],
    instructions:'You are a helpful customer support agent for KRE Observability. Always be polite, concise, and escalate to human agents when the issue requires account changes or billing disputes.',
  },
  {
    id:'agt-code-002', name:'Code Assistant', provider:'anthropic',
    model:'claude-3-5-sonnet', assistantId:'anthropic:claude-3-5-sonnet-20241022',
    status:'active', env:'production', version:'v1.8.0',
    description:'Code review, debugging, architecture suggestions, SQL generation',
    tools:['code_execution','bash','file_read'],
    knowledgeBases:['internal-codebase-docs','architecture-decisions'],
    compliance:['SOC2'],
    owner:'Engineering Enablement', costCenter:'ENG-PLATFORM',
    temperature:0.1, maxTokens:8192, contextWindow:200000,
    requestsToday:642, requestsMonth:18900, errRate:1.2,
    avgLatencyMs:2180, p95LatencyMs:5200,
    costToday:42.80, costMonth:1240.20, tokensMillion:34.8,
    healthScore:96.5, slaTarget:99.0, slaActual:98.8,
    lastActive:'8m ago', createdAt:'2024-09-22', lastDeployed:'1 week ago',
    tags:['developer-tool','code-gen','internal'],
    instructions:'You are an expert software engineer assistant. Prefer idiomatic code, always explain your reasoning, and flag potential security vulnerabilities.',
  },
  {
    id:'agt-da-003', name:'Data Analyst Agent', provider:'vertex',
    model:'gemini-1.5-pro', assistantId:'projects/kre-observability/locations/us-central1/agents/da-agent-003',
    status:'active', env:'production', version:'v1.2.3',
    description:'Natural-language querying of Snowflake warehouse, report generation',
    tools:['bigquery','sql_execution','chart_generation','email_send'],
    knowledgeBases:['warehouse-schema-catalog','metric-definitions'],
    compliance:['SOC2','GDPR'],
    owner:'Data & Analytics', costCenter:'DATA-ENG',
    temperature:0.2, maxTokens:4096, contextWindow:1000000,
    requestsToday:284, requestsMonth:7820, errRate:2.1,
    avgLatencyMs:3400, p95LatencyMs:8100,
    costToday:12.30, costMonth:380.60, tokensMillion:8.2,
    healthScore:94.1, slaTarget:98.0, slaActual:97.9,
    lastActive:'24m ago', createdAt:'2025-01-08', lastDeployed:'2 weeks ago',
    tags:['analytics','sql-gen','reporting'],
    instructions:'You are a data analyst with full access to the Snowflake data warehouse. Generate accurate SQL, verify row counts before reporting, and always cite data freshness.',
  },
  {
    id:'agt-docqa-004', name:'Document QA Agent', provider:'bedrock',
    model:'claude-3-haiku', assistantId:'arn:aws:bedrock:us-east-1::agent/ABCDEF123',
    status:'active', env:'production', version:'v3.1.0',
    description:'RAG-powered Q&A over internal knowledge base, contracts, runbooks',
    tools:['retrieval','citation','summarize'],
    knowledgeBases:['contracts-kb','runbooks-kb','compliance-docs','vendor-sla'],
    compliance:['SOC2','GDPR','HIPAA'],
    owner:'Legal & Compliance', costCenter:'LEGAL',
    temperature:0.0, maxTokens:2048, contextWindow:200000,
    requestsToday:428, requestsMonth:12400, errRate:0.4,
    avgLatencyMs:840, p95LatencyMs:2100,
    costToday:4.10, costMonth:124.80, tokensMillion:4.1,
    healthScore:99.4, slaTarget:99.9, slaActual:99.8,
    lastActive:'5m ago', createdAt:'2024-08-14', lastDeployed:'1 month ago',
    tags:['rag','legal','compliance','zero-hallucination'],
    instructions:'You are a precise document retrieval assistant. Only answer from retrieved context. If context does not contain the answer, say so explicitly. Always cite document and page number.',
  },
  {
    id:'agt-email-005', name:'Email Drafter', provider:'openai',
    model:'gpt-4o-mini', assistantId:'asst_Yz4nWx1rBmPq7dK8',
    status:'degraded', env:'staging', version:'v0.9.2-beta',
    description:'Drafts professional emails, follow-ups, and meeting summaries',
    tools:['email_read','calendar_read','contacts'],
    knowledgeBases:['email-templates','brand-voice-guide'],
    compliance:['GDPR'],
    owner:'Productivity Engineering', costCenter:'PROD-ENG',
    temperature:0.7, maxTokens:1024, contextWindow:128000,
    requestsToday:182, requestsMonth:3400, errRate:8.4,
    avgLatencyMs:920, p95LatencyMs:2800,
    costToday:1.20, costMonth:28.40, tokensMillion:1.8,
    healthScore:72.3, slaTarget:99.0, slaActual:91.6,
    lastActive:'1h ago', createdAt:'2025-03-01', lastDeployed:'2 days ago',
    tags:['productivity','email','beta'],
    instructions:'Draft professional, concise emails in the user\'s voice. Prefer brevity. Always give 3 subject line options.',
  },
  {
    id:'agt-sec-006', name:'Security Scanner Agent', provider:'anthropic',
    model:'claude-3-haiku', assistantId:'anthropic:claude-3-haiku-20240307',
    status:'active', env:'production', version:'v1.0.4',
    description:'Automated code security review, SAST findings triage, CVE analysis',
    tools:['code_read','sast_run','cve_lookup','jira_create'],
    knowledgeBases:['owasp-top10','cve-database','internal-security-policy'],
    compliance:['SOC2','PCI-DSS'],
    owner:'Security Engineering', costCenter:'SEC-ENG',
    temperature:0.0, maxTokens:4096, contextWindow:200000,
    requestsToday:94, requestsMonth:2840, errRate:0.0,
    avgLatencyMs:1820, p95LatencyMs:4200,
    costToday:2.80, costMonth:84.20, tokensMillion:2.2,
    healthScore:100, slaTarget:99.9, slaActual:100,
    lastActive:'18m ago', createdAt:'2025-02-10', lastDeployed:'3 weeks ago',
    tags:['security','sast','zero-false-negative'],
    instructions:'You are a security engineer reviewing code for vulnerabilities. Be thorough, cite CWE IDs, assess severity per CVSS 3.1, and never mark a high-severity finding as informational.',
  },
  {
    id:'agt-search-007', name:'Enterprise Search Agent', provider:'selfhosted',
    model:'llama-3.1-70b', assistantId:'k8s:llm-serving/llama-70b-agent-007',
    status:'active', env:'development', version:'v0.3.1-dev',
    description:'Internal semantic search across Confluence, Jira, GitHub, Slack',
    tools:['confluence_search','jira_search','github_search','slack_search'],
    knowledgeBases:[],
    compliance:[],
    owner:'Platform R&D', costCenter:'RD-ENG',
    temperature:0.5, maxTokens:2048, contextWindow:32000,
    requestsToday:38, requestsMonth:820, errRate:12.0,
    avgLatencyMs:4200, p95LatencyMs:12000,
    costToday:0, costMonth:0, tokensMillion:0.4,
    healthScore:61.4, slaTarget:95.0, slaActual:88.0,
    lastActive:'3h ago', createdAt:'2025-05-20', lastDeployed:'today',
    tags:['search','experimental','self-hosted'],
    instructions:'Search across internal tools and return the top 5 most relevant results with source links.',
  },
  {
    id:'agt-rpt-008', name:'Report Generation Agent', provider:'openai',
    model:'gpt-4o', assistantId:'asst_Mn5tQd8xVrLk3pW2',
    status:'inactive', env:'production', version:'v1.5.0',
    description:'Weekly executive report compilation: KPIs, incidents, and forecasts',
    tools:['sql_execution','chart_generation','pdf_create','email_send'],
    knowledgeBases:['kpi-definitions','report-templates','executive-context'],
    compliance:['SOC2'],
    owner:'Finance & Strategy', costCenter:'FIN-OPS',
    temperature:0.2, maxTokens:16384, contextWindow:128000,
    requestsToday:0, requestsMonth:84, errRate:0.0,
    avgLatencyMs:12400, p95LatencyMs:28000,
    costToday:0, costMonth:640.20, tokensMillion:18.2,
    healthScore:100, slaTarget:99.0, slaActual:100,
    lastActive:'2 days ago', createdAt:'2024-12-01', lastDeployed:'2 months ago',
    tags:['reporting','scheduled','executive'],
    instructions:'Generate professional executive reports. Use data from the warehouse, always include trend commentary, and highlight anomalies. Format for PDF export.',
  },
];

// ─── Conversation history data ────────────────────────────────────────────────
function makeConvs(agentId) {
  const topics = [
    { topic:'Order refund request', turns:[
      { role:'user',  text:'I ordered item #48291 3 weeks ago but never received it. Can I get a refund?' },
      { role:'tool',  name:'file_search', input:'order #48291', output:'Order status: Delivered 2025-06-02 via FedEx tracking 7482920183' },
      { role:'agent', text:'I found your order #48291. According to our records, it was marked delivered on June 2nd via FedEx (tracking: 7482920183). I\'d recommend checking with neighbors or your building front desk first. If it\'s still missing, I can escalate this to our fulfillment team for a reshipping or full refund.' },
      { role:'user',  text:'I checked and it definitely wasn\'t delivered. Please issue a refund.' },
      { role:'tool',  name:'file_search', input:'refund policy order delivered not received', output:'Policy: If delivery confirmed but item not received, escalate to L2 within 48h. Refund authorized after carrier investigation (3-5 business days).' },
      { role:'agent', text:'Understood. I\'ve escalated this to our fulfillment team (ticket #T-82910). A refund will be processed within 3-5 business days pending the carrier investigation. You\'ll receive a confirmation email at the address on file.' },
    ], tokens:840, cost:0.021, status:'completed', time:'14m ago' },
    { topic:'Product configuration help', turns:[
      { role:'user',  text:'How do I configure the alerting thresholds in KRE Observability?' },
      { role:'tool',  name:'file_search', input:'alerting thresholds configuration', output:'Doc: Alert Configuration — Settings > Alerts > Thresholds. Set per-metric, supports absolute and percentage-based.' },
      { role:'agent', text:'To configure alerting thresholds in KRE Observability:\n1. Go to **Settings > Alerts > Thresholds**\n2. Select the metric you want to alert on\n3. Choose "Absolute" for fixed values or "Percentage" for relative changes\n4. Set your warning and critical levels\n\nWould you like a walkthrough for a specific metric?' },
    ], tokens:420, cost:0.009, status:'completed', time:'2h ago' },
  ];
  return topics.map((t, i) => ({ id:`conv-${agentId}-${i}`, agentId, ...t }));
}

// ─── Audit log data ───────────────────────────────────────────────────────────
function makeAuditLog(agent) {
  return [
    { ts:'3d ago',   user:'sarah.m@company.com',  action:'deploy',        detail:`Deployed ${agent.version} — updated system instructions` },
    { ts:'1w ago',   user:'rahul.k@company.com',   action:'config_change', detail:'Updated temperature: 0.5 → 0.3, maxTokens: 2048 → 4096' },
    { ts:'2w ago',   user:'ops-pipeline',          action:'auto_scale',    detail:'Min instances scaled 1→2 due to p95 latency breach (5.8s > 5.0s SLA)' },
    { ts:'3w ago',   user:'james.t@company.com',   action:'kb_update',     detail:`Knowledge base "${agent.knowledgeBases?.[0] || 'primary-kb'}" reindexed — 284 new documents` },
    { ts:'1mo ago',  user:'admin@company.com',     action:'access_grant',  detail:'Added team "platform-eng" to agent access list' },
    { ts:'2mo ago',  user:'sarah.m@company.com',  action:'create',        detail:`Agent created — initial version v1.0.0` },
  ];
}

// ─── Debug events data ────────────────────────────────────────────────────────
const DEBUG_EVENTS_BASE = [
  { type:'llm_request',  level:'INFO',  msg:'LLM API call initiated',                   detail:'model=gpt-4o tokens_estimate=1240 stream=true' },
  { type:'cache_check',  level:'DEBUG', msg:'Prompt cache lookup: MISS',                detail:'cache_key=sha256:8f3a... ttl=300s' },
  { type:'safety_pre',   level:'INFO',  msg:'Pre-flight safety check passed',            detail:'toxicity=0.02 pii=false injection=false latency=12ms' },
  { type:'tool_call',    level:'INFO',  msg:'Tool invoked: file_search',                 detail:'query="order #48291" index=product-docs top_k=5' },
  { type:'tool_result',  level:'INFO',  msg:'Tool returned 3 results (42ms)',            detail:'hits=[{score:0.94,doc:"orders/48291.json"},{score:0.88,...}]' },
  { type:'llm_response', level:'INFO',  msg:'LLM stream complete',                       detail:'output_tokens=284 latency=1240ms finish_reason=stop' },
  { type:'safety_post',  level:'INFO',  msg:'Post-generation safety check passed',       detail:'groundedness=0.91 relevance=0.88 faithfulness=0.93 latency=18ms' },
  { type:'trace_saved',  level:'DEBUG', msg:'Trace persisted to observability store',   detail:'trace_id=A8F2K9P1 span_count=5 cost=$0.021' },
  { type:'llm_request',  level:'INFO',  msg:'LLM API call initiated',                   detail:'model=gpt-4o tokens_estimate=920 stream=true' },
  { type:'cache_check',  level:'DEBUG', msg:'Prompt cache lookup: HIT',                 detail:'cache_key=sha256:4d1b... saved=840 tokens cost_saved=$0.012' },
  { type:'error',        level:'ERROR', msg:'Tool execution failed: code_interpreter',  detail:'error=TimeoutError duration=30001ms retry=1/3' },
  { type:'tool_call',    level:'INFO',  msg:'Tool invoked: web_search',                 detail:'query="FedEx tracking 7482920183" max_results=3' },
  { type:'safety_post',  level:'WARN',  msg:'Groundedness score below threshold',       detail:'groundedness=0.62 threshold=0.75 — flagged for review' },
  { type:'llm_response', level:'INFO',  msg:'LLM stream complete',                      detail:'output_tokens=142 latency=820ms finish_reason=stop' },
];

const EVENT_TYPE_COLOR = {
  llm_request:  '#60a5fa', llm_response: '#34d399', cache_check: '#94a3b8',
  safety_pre:   '#22c55e', safety_post:  '#fbbf24',  tool_call:   '#c084fc',
  tool_result:  '#a78bfa', trace_saved:  '#64748b',   error:       '#f87171',
};
const LEVEL_COLOR = { INFO:'#60a5fa', DEBUG:'#64748b', WARN:'#fbbf24', ERROR:'#f87171' };

// ─── Token pricing per model ($ per 1M tokens) ────────────────────────────────
const MODEL_PRICING = {
  'gpt-4o':            { inPer1M: 5.00,  outPer1M: 15.00 },
  'gpt-4o-mini':       { inPer1M: 0.15,  outPer1M: 0.60  },
  'claude-3-5-sonnet': { inPer1M: 3.00,  outPer1M: 15.00 },
  'claude-3-haiku':    { inPer1M: 0.25,  outPer1M: 1.25  },
  'gemini-1.5-pro':    { inPer1M: 3.50,  outPer1M: 10.50 },
  'llama-3.1-70b':     { inPer1M: 0,     outPer1M: 0     },
};

function tokenCost(model, inM, outM) {
  const pr = MODEL_PRICING[model] || { inPer1M:0, outPer1M:0 };
  const inCost  = inM  * pr.inPer1M;
  const outCost = outM * pr.outPer1M;
  return { inCost, outCost, total: inCost + outCost };
}

// ─── Simulated test responses per agent ───────────────────────────────────────
function getTestResponse(agentId, prompt) {
  const p = prompt.toLowerCase();
  const R = {
    'agt-cs-001': p.includes('refund') || p.includes('order')
      ? "I've located your order in our system. The tracking shows delivery was marked complete, but since you're reporting non-receipt I've opened escalation ticket #T-92847. A refund will be processed within 3–5 business days once the carrier investigation completes. You'll receive a confirmation email at the address on file."
      : "Thank you for reaching out! I can help you with that. Our support team is available Monday–Friday 9AM–6PM EST. For immediate account, billing, or technical issues, you can also reach us at support@krelixir.com or via in-app chat. Is there anything specific I can resolve right now?",
    'agt-code-002': p.includes('factorial')
      ? "**Issue: Missing base case — causes infinite recursion**\n\n```python\n# ❌ Current — RecursionError on any input\ndef factorial(n):\n    return n * factorial(n - 1)\n\n# ✅ Fixed — base case + input validation\ndef factorial(n: int) -> int:\n    if not isinstance(n, int) or n < 0:\n        raise ValueError(f\"Expected non-negative int, got {n!r}\")\n    return 1 if n <= 1 else n * factorial(n - 1)\n```\n\n**Issues found:** (1) No base case → stack overflow. (2) No type check. (3) Consider iterative version for n>500 to avoid Python's 1000-frame recursion limit."
      : "**Code Review Complete**\n\nKey findings:\n1. ⚠️ **Security**: Potential injection on line 3 — use parameterized queries\n2. ✅ **Logic**: Business logic appears correct\n3. 💡 **Performance**: Cache the expensive computation on line 12\n4. 📝 **Style**: Rename to follow snake_case convention\n\nOverall: **Requires changes before merge**",
    'agt-da-003': "I'll run that against the Snowflake warehouse.\n\n```sql\nSELECT\n    c.company_name,\n    r.mrr_usd,\n    ROUND(r.mrr_usd / SUM(r.mrr_usd) OVER () * 100, 2) AS pct_total,\n    r.mrr_usd - LAG(r.mrr_usd) OVER (PARTITION BY c.id ORDER BY r.month) AS mom_delta\nFROM dim_customers c\nJOIN fact_revenue r ON c.id = r.customer_id\nWHERE r.month = DATE_TRUNC('month', CURRENT_DATE)\nORDER BY r.mrr_usd DESC LIMIT 10;\n```\n\n**Results** (Snowflake · data age: 2m)\n| # | Customer | MRR | % Total | MoM |\n|---|---|---|---|---|\n| 1 | Acme Corp | $48,200 | 12.4% | +8.2% |\n| 2 | TechFlow Inc | $38,400 | 9.9% | +2.1% |\n| 3 | DataSystems | $31,200 | 8.0% | −1.4% |",
    'agt-docqa-004': "**Retrieved from knowledge base** (3 chunks, relevance ≥ 0.88)\n\n> *Source: AWS Enterprise Agreement v4.2 § 8.1 (SLA)*\n> \"AWS will use commercially reasonable efforts to make the Covered Service available with a Monthly Uptime Percentage of at least **99.99%** for each AWS region.\"\n\n**Key terms:**\n- Service Credits: 10% of monthly fees for 99.0–99.99%; 25% for <99.0%\n- Claim window: Must be filed within **30 days** of the incident\n- P1 response target: 1 hour response · 4 hour resolution\n- Exclusions: Scheduled maintenance, force majeure, customer-caused outages\n\n*Citation: AWS_Enterprise_Agreement_v4.2.pdf pp. 34–36 · retrieved 2026-06-10*",
    'agt-email-005': "**Subject line options:**\n1. \"Following up on our proposal — next steps\"\n2. \"Quick check-in: KRE Observability proposal from June 6th\"\n3. \"Re: Enterprise observability — your thoughts?\"\n\n---\nHi [Name],\n\nI wanted to follow up on the proposal I sent over on June 6th regarding KRE Observability's enterprise observability platform for [Company].\n\nI know you're busy — do you have 15 minutes this week to discuss any questions or confirm next steps?\n\nBest,\n[Your name]",
    'agt-sec-006': "**🚨 CRITICAL: SQL Injection — CWE-89**\n\n```\nCVSS 3.1: 9.8 (CRITICAL)\nAV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H\n```\n\n**Vulnerable line:**\n```javascript\nconst query = \"SELECT * FROM users WHERE id = \" + userId; // ❌\n```\n\nAttacker passes `userId = \"1 OR 1=1\"` → full table dump. Or `\"1; DROP TABLE users--\"` → destructive.\n\n**Fix:**\n```javascript\nconst query = \"SELECT * FROM users WHERE id = $1\"; // ✅\nawait db.query(query, [userId]);\n```\n\n**Jira ticket created:** SEC-4821 · P1 · Block deploy until resolved.",
    'agt-search-007': "**Top 5 results for your query:**\n\n1. **[Jira] PAY-2841** — Payment failures after Stripe API v5 migration\n   *2026-06-08 · In Progress · rahul.k*\n\n2. **[Jira] PAY-2839** — Intermittent 402 errors on subscription renewal\n   *2026-06-07 · Open · High priority*\n\n3. **[Confluence]** Stripe Webhook Failure Runbook\n   *Updated 2026-06-01 · 48 views this week*\n\n4. **[GitHub] PR #2841** — Fix payment retry with exponential backoff\n   *Merged 2026-06-05 · james.t · 12 files changed*\n\n5. **[Slack] #payment-alerts** — 14 messages about gateway timeouts\n   *Most recent 3h ago*",
    'agt-rpt-008': "# Weekly KPI Report — Week Ending June 9, 2026\n\n## Summary\nStrong week. Revenue +5.2% vs target. SLA dipped to 99.2% (INC-2041 — resolved).\n\n| Metric | Actual | Target | Δ |\n|---|---|---|---|\n| Weekly Revenue | $1.21M | $1.15M | ✅ +5.2% |\n| Active Users | 18,420 | 18,000 | ✅ +2.3% |\n| SLA Compliance | 99.2% | 99.5% | ⚠️ −0.3% |\n| CSAT | 4.6/5 | 4.5/5 | ✅ +2.2% |\n\n**Incidents:** INC-2041 (Critical) — DB pool exhaustion, 23min impact, RCA complete.\n\n**Next week:** Deploy cert-manager fix · Scale payment-api for Q3 (+40% traffic).",
  };
  return R[agentId] || `I've processed your query: "${prompt}"\n\nBased on my knowledge base and available tools, here is my response. This is a simulated demonstration — in production I would provide a fully grounded, cited answer drawn from configured knowledge sources and real-time tool access.`;
}

function getTestTools(agentId, prompt) {
  const p = prompt.toLowerCase();
  const T = {
    'agt-cs-001':    (p.includes('order')||p.includes('refund')) ? [{ name:'file_search',     q:`"${prompt.slice(0,38)}"`,   result:'3 docs retrieved (score>0.88)',                     ms:42  }] : [],
    'agt-code-002':  [{ name:'code_execution', q:'Analyzing submitted snippet',              result:'Static analysis complete — issues found',              ms:180 }],
    'agt-da-003':    [{ name:'sql_execution',  q:`Query: "${prompt.slice(0,38)}"`,           result:'Executed in 340ms · 10 rows returned',                 ms:340 }],
    'agt-docqa-004': [{ name:'retrieval',      q:`semantic: "${prompt.slice(0,38)}"`,        result:'4 chunks from 2 docs retrieved (relevance>0.85)',      ms:68  }],
    'agt-email-005': [{ name:'email_read',     q:'Recent sent threads (last 7d)',            result:'3 related threads found',                              ms:24  }],
    'agt-sec-006':   [{ name:'sast_run',       q:'Scanning code snippet',                   result:'1 critical finding (CWE-89)',                          ms:220 },
                     { name:'cve_lookup',      q:'SQL injection CVE lookup',                result:'CVE match: CVSS 9.8',                                  ms:55  }],
    'agt-search-007':[{ name:'jira_search',    q:`"${prompt.slice(0,30)}"`,                 result:'12 tickets matched',                                  ms:180 },
                     { name:'confluence_search',q:`"${prompt.slice(0,30)}"`,                result:'3 pages found',                                        ms:120 },
                     { name:'github_search',   q:`"${prompt.slice(0,30)}"`,                 result:'4 PRs / commits found',                                ms:90  }],
    'agt-rpt-008':   [{ name:'sql_execution',  q:'Fetch weekly KPIs from Snowflake',        result:'14 metrics retrieved (340ms)',                         ms:340 },
                     { name:'chart_generation',q:'Render KPI trend charts',                 result:'4 charts generated (PNG)',                             ms:820 }],
  };
  return T[agentId] || [];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtMs(ms) { return ms >= 1000 ? `${(ms/1000).toFixed(2)}s` : `${ms}ms`; }
function fmtCost(c) { return c === 0 ? '$0' : c >= 1 ? `$${c.toFixed(2)}` : `$${c.toFixed(3)}`; }
function fmtTok(n)  { return n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n); }

// ─── Reusable UI ──────────────────────────────────────────────────────────────
function ProviderBadge({ provider, size = 'sm' }) {
  const p = PROVIDERS[provider] || { label: provider, color: '#94a3b8', bg: '#94a3b812', icon: '🤖' };
  return (
    <span style={{ fontSize: size === 'xs' ? 9 : 10, fontWeight: 700, padding: size === 'xs' ? '1px 5px' : '2px 7px',
      borderRadius: 4, background: p.bg, color: p.color, border: `1px solid ${p.color}25`,
      display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: size === 'xs' ? 9 : 11 }}>{p.icon}</span> {p.label}
    </span>
  );
}

function StatusDot({ status }) {
  const s = STATUS_CFG[status] || STATUS_CFG.inactive;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: s.color }}>
      {s.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, display: 'inline-block',
        boxShadow: `0 0 6px ${s.color}80` }}/>}
      {s.label}
    </span>
  );
}

function HealthRing({ score, size = 44 }) {
  const r = (size - 6) / 2, c = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 90 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--border)" strokeWidth={4}/>
      <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${c} ${c})`}/>
      <text x={c} y={c + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize={9} fontWeight={700} fill={color} fontFamily="Space Mono,monospace">{score.toFixed(0)}</text>
    </svg>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ agent }) {
  const p = PROVIDERS[agent.provider];
  const slaOk = agent.slaActual >= agent.slaTarget;
  const inM    = parseFloat((agent.tokensMillion * 0.65).toFixed(2));
  const outM   = parseFloat((agent.tokensMillion * 0.35).toFixed(2));
  const costs  = tokenCost(agent.model, inM, outM);
  const pr     = MODEL_PRICING[agent.model] || { inPer1M:0, outPer1M:0 };
  const isFree = pr.inPer1M === 0 && pr.outPer1M === 0;
  const costPerReq = agent.requestsMonth > 0 ? costs.total / agent.requestsMonth : 0;
  return (
    <div className="flex flex-col gap-4">
      {/* Description + tags */}
      <div className="rounded-xl p-4" style={{ background: `${p.color}07`, border: `1px solid ${p.color}20` }}>
        <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--text-2)' }}>{agent.description}</p>
        <div className="flex flex-wrap gap-1.5">
          {agent.tags.map(t => (
            <span key={t} style={{ fontSize: 9, padding: '1px 7px', borderRadius: 20, background: `${p.color}15`, color: p.color, fontWeight: 700, border: `1px solid ${p.color}25` }}>#{t}</span>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Requests Today', value: agent.requestsToday.toLocaleString(), color:'#60a5fa' },
          { label:'Error Rate',     value: `${agent.errRate}%`,                 color: agent.errRate > 5 ? '#f87171' : '#34d399' },
          { label:'Avg Latency',    value: fmtMs(agent.avgLatencyMs),           color:'#fbbf24' },
          { label:'Cost / Month',   value: fmtCost(agent.costMonth),            color:'#c084fc' },
          { label:'P95 Latency',    value: fmtMs(agent.p95LatencyMs),          color:'#f97316' },
          { label:'SLA Actual',     value: `${agent.slaActual}%`,               color: slaOk ? '#22c55e' : '#f87171' },
          { label:'Context Window', value: `${(agent.contextWindow/1000).toFixed(0)}K`, color:'#94a3b8' },
          { label:'Tokens (Mo)',    value: `${agent.tokensMillion}M`,            color:'#60a5fa' },
        ].map(k => (
          <div key={k.label} className="rounded-lg p-2.5 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
            <div className="mono font-black text-base mb-0.5" style={{ color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 8, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Token Economics */}
      <div>
        <div className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
          <DollarSign size={10}/> Token Economics (Monthly)
        </div>
        {/* Table */}
        <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
          <div className="grid px-4 py-2" style={{ gridTemplateColumns:'1fr 80px 100px 80px', background:'var(--bg-tertiary)', borderBottom:'1px solid var(--border)' }}>
            {['Token Type','Volume','Rate / 1M','Cost'].map(h => (
              <span key={h} style={{ fontSize:9, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px' }}>{h}</span>
            ))}
          </div>
          {/* Input row */}
          <div className="grid px-4 py-3 items-center" style={{ gridTemplateColumns:'1fr 80px 100px 80px', borderBottom:'1px solid rgba(255,255,255,.035)' }}>
            <div className="flex items-center gap-2">
              <div style={{ width:8, height:8, borderRadius:2, background:'#60a5fa', flexShrink:0 }}/>
              <span className="text-xs font-semibold">Input Tokens</span>
            </div>
            <span className="mono text-xs" style={{ color:'#60a5fa' }}>{inM}M</span>
            <span className="mono text-xs" style={{ color:'var(--text-3)' }}>{isFree ? 'Free' : `$${pr.inPer1M.toFixed(2)}`}</span>
            <span className="mono text-xs font-bold" style={{ color:'#60a5fa' }}>{isFree ? '—' : `$${costs.inCost.toFixed(2)}`}</span>
          </div>
          {/* Output row */}
          <div className="grid px-4 py-3 items-center" style={{ gridTemplateColumns:'1fr 80px 100px 80px', borderBottom:'1px solid rgba(255,255,255,.035)', background:'rgba(255,255,255,.01)' }}>
            <div className="flex items-center gap-2">
              <div style={{ width:8, height:8, borderRadius:2, background:'#c084fc', flexShrink:0 }}/>
              <span className="text-xs font-semibold">Output Tokens</span>
            </div>
            <span className="mono text-xs" style={{ color:'#c084fc' }}>{outM}M</span>
            <span className="mono text-xs" style={{ color:'var(--text-3)' }}>{isFree ? 'Free' : `$${pr.outPer1M.toFixed(2)}`}</span>
            <span className="mono text-xs font-bold" style={{ color:'#c084fc' }}>{isFree ? '—' : `$${costs.outCost.toFixed(2)}`}</span>
          </div>
          {/* Total row */}
          <div className="grid px-4 py-3 items-center" style={{ gridTemplateColumns:'1fr 80px 100px 80px', background:'rgba(34,197,94,.04)' }}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold">Total</span>
              {!isFree && <span style={{ fontSize:8, color:'var(--text-3)' }}>blended ${(costs.total/agent.tokensMillion).toFixed(2)}/1M</span>}
            </div>
            <span className="mono text-xs font-bold">{agent.tokensMillion}M</span>
            <span/>
            <span className="mono font-black" style={{ fontSize:15, color:'#34d399' }}>{isFree ? '$0.00' : `$${costs.total.toFixed(2)}`}</span>
          </div>
        </div>
        {/* Cost summary chips */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          {[
            { l:'Cost per Request', v: isFree ? '$0.0000' : `$${costPerReq.toFixed(4)}`,                         c:'#fbbf24' },
            { l:'Cost Today',       v: isFree ? '$0.00'   : `$${agent.costToday.toFixed(2)}`,                    c:'#f97316' },
            { l:'Projected Annual', v: isFree ? '$0'      : `$${(costs.total * 12).toLocaleString('en-US', { maximumFractionDigits:0 })}`, c:'#ef4444' },
          ].map(k => (
            <div key={k.l} className="rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:`1px solid ${k.c}18` }}>
              <div className="mono font-black text-base mb-0.5" style={{ color: k.c }}>{k.v}</div>
              <div style={{ fontSize:8, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{k.l}</div>
            </div>
          ))}
        </div>
        {/* Token split bar */}
        <div className="mt-3 rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-2" style={{ fontSize:10, color:'var(--text-3)' }}>
            <span>Token Split</span>
            <span>Input 65% / Output 35%</span>
          </div>
          <div className="flex rounded-full overflow-hidden" style={{ height:6, background:'var(--bg-primary)' }}>
            <div style={{ width:'65%', background:'#60a5fa' }}/>
            <div style={{ width:'35%', background:'#c084fc' }}/>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span style={{ fontSize:9, color:'#60a5fa' }}>● Input {inM}M tokens</span>
            <span style={{ fontSize:9, color:'#c084fc' }}>Output {outM}M tokens ●</span>
          </div>
        </div>
      </div>

      {/* Tools + Knowledge Bases */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
            <Zap size={10}/> Tools ({agent.tools.length})
          </div>
          <div className="flex flex-col gap-1">
            {agent.tools.map(tool => (
              <div key={tool} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div style={{ width:6, height:6, borderRadius:'50%', background: p.color, flexShrink:0 }}/>
                <span className="mono text-xs" style={{ color:'var(--text-2)' }}>{tool}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
            <Database size={10}/> Knowledge Bases ({agent.knowledgeBases.length})
          </div>
          {agent.knowledgeBases.length === 0 ? (
            <div className="text-xs px-3 py-2 rounded-lg" style={{ color:'var(--text-3)', background:'var(--bg-tertiary)' }}>No knowledge bases attached</div>
          ) : agent.knowledgeBases.map(kb => (
            <div key={kb} className="flex items-center gap-2 px-3 py-1.5 rounded-lg mb-1" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
              <Database size={9} style={{ color:'#60a5fa', flexShrink:0 }}/>
              <span className="mono text-xs" style={{ color:'var(--text-2)' }}>{kb}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance + meta */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
            <Shield size={10}/> Compliance
          </div>
          {agent.compliance.length === 0 ? (
            <span style={{ fontSize:10, color:'var(--text-3)' }}>No compliance tags</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {agent.compliance.map(c => (
                <span key={c} style={{ fontSize:9, padding:'2px 8px', borderRadius:20, background:'rgba(34,197,94,.12)', color:'#4ade80', fontWeight:700, border:'1px solid rgba(34,197,94,.25)' }}>{c}</span>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
          <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
            <Users size={10}/> Ownership
          </div>
          <div className="space-y-1">
            {[
              { l:'Owner', v: agent.owner },
              { l:'Cost Center', v: agent.costCenter },
              { l:'Version', v: agent.version },
              { l:'Deployed', v: agent.lastDeployed },
            ].map(r => (
              <div key={r.l} className="flex items-center justify-between text-xs">
                <span style={{ color:'var(--text-3)' }}>{r.l}</span>
                <span className="font-semibold mono" style={{ color:'var(--text-2)' }}>{r.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System instructions preview */}
      <div>
        <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
          <FileText size={10}/> System Instructions
        </div>
        <div className="rounded-xl p-3" style={{ background:'var(--bg-primary)', border:'1px solid var(--border)', fontFamily:'Space Mono, monospace', fontSize:11, color:'var(--text-2)', lineHeight:1.7 }}>
          {agent.instructions}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Test ────────────────────────────────────────────────────────────────
const EXAMPLE_PROMPTS = {
  'agt-cs-001':    ['Refund for order #48291 not received', 'How to configure alert thresholds?', 'Upgrade my plan to Enterprise'],
  'agt-code-002':  ['Review: def factorial(n): return n*factorial(n-1)', 'Write async Python HTTP client with retry logic', 'Optimize this slow JOIN query'],
  'agt-da-003':    ['Top 10 customers by MRR this month', 'Churn trend last 6 months by segment', 'Average deal size by sales rep Q2'],
  'agt-docqa-004': ['What does the AWS SLA say about uptime?', 'HIPAA data retention requirements', 'Key renewal terms in vendor contract'],
  'agt-email-005': ['Follow-up on proposal sent 3 days ago', 'Decline a meeting request politely', 'Thank-you note after a successful demo'],
  'agt-sec-006':   ['Review: const q = "SELECT * WHERE id=" + id', 'CVE-2024-3094 impact on our stack', 'Is our S3 config OWASP compliant?'],
  'agt-search-007':['Recent Jira tickets about payment failures', 'Confluence docs on engineer onboarding', 'GitHub PRs merged last week in billing'],
  'agt-rpt-008':   ['Weekly KPI summary ending June 9', 'Q2 2025 performance vs Q1 targets', 'Top 3 incidents this month executive view'],
};

function TestTab({ agent }) {
  const [prompt, setPrompt]       = useState('');
  const [running, setRunning]     = useState(false);
  const [phase, setPhase]         = useState('idle');
  const [streamText, setStreamText] = useState('');
  const [liveTools, setLiveTools] = useState([]);
  const [history, setHistory]     = useState([]);
  const streamRef                 = useRef(null);
  const p = PROVIDERS[agent.provider];
  const pr = MODEL_PRICING[agent.model] || { inPer1M: 0, outPer1M: 0 };
  const isFree = pr.inPer1M === 0 && pr.outPer1M === 0;
  const examples = EXAMPLE_PROMPTS[agent.id] || ['What can you help me with?', 'Summarize the current status', 'Run a diagnostic check'];

  const runTest = () => {
    if (!prompt.trim() || running) return;
    const userPrompt = prompt.trim();
    setRunning(true);
    setPhase('thinking');
    setStreamText('');
    setLiveTools([]);

    const tools    = getTestTools(agent.id, userPrompt);
    const response = getTestResponse(agent.id, userPrompt);
    const startMs  = Date.now();

    // Show tool calls one by one, then stream the response
    const kickTools = (idx) => {
      if (idx < tools.length) {
        setPhase('tools');
        setLiveTools(prev => [...prev, { ...tools[idx], done: false }]);
        setTimeout(() => {
          setLiveTools(prev => prev.map((t, i) => i === idx ? { ...t, done: true } : t));
          setTimeout(() => kickTools(idx + 1), 200);
        }, tools[idx].ms);
      } else {
        startStream(response, startMs);
      }
    };

    setTimeout(() => {
      if (tools.length > 0) kickTools(0);
      else startStream(response, startMs);
    }, 500);

    function startStream(responseText, t0) {
      setPhase('streaming');
      const words = responseText.split(' ');
      let i = 0;
      streamRef.current = setInterval(() => {
        setStreamText(prev => prev + (i > 0 ? ' ' : '') + words[i]);
        i++;
        if (i >= words.length) {
          clearInterval(streamRef.current);
          const latencyMs = Date.now() - t0;
          const inTokens  = Math.ceil((userPrompt.length + (agent.instructions || '').length) / 4) + 60;
          const outTokens = Math.ceil(responseText.length / 4);
          const inCost    = (inTokens  / 1_000_000) * pr.inPer1M;
          const outCost   = (outTokens / 1_000_000) * pr.outPer1M;
          setHistory(prev => [{
            id: Date.now(), prompt: userPrompt, response: responseText,
            tools: [...tools], inTokens, outTokens,
            inCost, outCost, totalCost: inCost + outCost,
            latencyMs, ts: new Date().toLocaleTimeString('en-US', { hour12: true }),
          }, ...prev.slice(0, 9)]);
          setRunning(false);
          setPhase('done');
          setStreamText('');
          setLiveTools([]);
          setPrompt('');
        }
      }, 38);
    }
  };

  const stop = () => {
    clearInterval(streamRef.current);
    setRunning(false);
    setPhase('idle');
    setStreamText('');
    setLiveTools([]);
  };

  // Current active run shown at top if streaming
  const isActive = running || phase === 'streaming';

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="rounded-xl p-4 flex items-start gap-3 shrink-0" style={{ background: `${p.color}08`, border: `1px solid ${p.color}25` }}>
        <Play size={14} style={{ color: p.color, marginTop: 2, flexShrink: 0 }}/>
        <div>
          <div className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Interactive Test Console</div>
          <div className="text-xs" style={{ color: 'var(--text-3)' }}>
            Runs against sandboxed agent config · model: <span className="mono" style={{ color: p.color }}>{agent.model}</span>
            {!isFree && <> · input <span className="mono" style={{ color:'#60a5fa' }}>${pr.inPer1M}/1M</span> · output <span className="mono" style={{ color:'#c084fc' }}>${pr.outPer1M}/1M</span></>}
            {isFree  && <> · <span style={{ color:'#34d399' }}>self-hosted — no token cost</span></>}
          </div>
        </div>
      </div>

      {/* Example prompts */}
      <div className="shrink-0">
        <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
          <Zap size={9}/> Quick Prompts
        </div>
        <div className="flex flex-wrap gap-2">
          {examples.map(ex => (
            <button key={ex} onClick={() => !running && setPrompt(ex)}
              style={{ fontSize:10, padding:'4px 10px', borderRadius:20, background:'var(--bg-tertiary)',
                border:'1px solid var(--border)', color:'var(--text-2)', cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.5 : 1 }}>
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* Prompt input */}
      <div className="shrink-0">
        <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
          <MessageSquare size={9}/> Prompt
        </div>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runTest(); }}
          disabled={running}
          rows={3}
          placeholder={`Ask ${agent.name}… (⌘↵ to run)`}
          style={{ width:'100%', padding:'10px 12px', borderRadius:10, resize:'vertical', fontSize:13,
            background:'var(--bg-primary)', border:'1px solid var(--border)', color:'var(--text)',
            fontFamily:'inherit', outline:'none', opacity: running ? 0.6 : 1, boxSizing:'border-box' }}
        />
        <div className="flex items-center justify-between mt-2">
          <span style={{ fontSize:10, color:'var(--text-3)' }}>{prompt.length > 0 ? `~${Math.ceil(prompt.length/4)} input tokens` : 'Type a prompt to test'}</span>
          <div className="flex gap-2">
            {running && (
              <button onClick={stop} style={{ fontSize:11, padding:'5px 14px', borderRadius:8,
                background:'rgba(239,68,68,.15)', border:'1px solid #ef444440', color:'#f87171', cursor:'pointer', fontWeight:600 }}>
                Stop
              </button>
            )}
            <button onClick={runTest} disabled={!prompt.trim() || running}
              style={{ fontSize:11, padding:'5px 16px', borderRadius:8, background: p.color, color:'#fff',
                border:'none', cursor: !prompt.trim() || running ? 'not-allowed' : 'pointer', fontWeight:700,
                opacity: !prompt.trim() || running ? 0.5 : 1, display:'flex', alignItems:'center', gap:6 }}>
              <Play size={10}/>
              {running ? (phase === 'thinking' ? 'Thinking…' : phase === 'tools' ? 'Running tools…' : 'Generating…') : 'Run Test'}
            </button>
          </div>
        </div>
      </div>

      {/* Live streaming panel */}
      {isActive && (
        <div className="rounded-xl overflow-hidden shrink-0" style={{ border:`1px solid ${p.color}35` }}>
          <div className="px-4 py-2 flex items-center gap-2" style={{ background:`${p.color}12`, borderBottom:`1px solid ${p.color}25` }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background: p.color, animation:'pulse 1s infinite' }}/>
            <span style={{ fontSize:10, fontWeight:700, color: p.color, textTransform:'uppercase', letterSpacing:'0.6px' }}>
              {phase === 'thinking' ? 'Processing…' : phase === 'tools' ? 'Executing tools…' : 'Streaming response…'}
            </span>
          </div>
          {/* Tool calls */}
          {liveTools.length > 0 && (
            <div className="px-4 py-2 flex flex-col gap-1.5" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-primary)' }}>
              {liveTools.map((t, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <span style={{ fontSize:9, padding:'1px 6px', borderRadius:4, background:'rgba(251,191,36,.12)', color:'#fbbf24', fontFamily:'Space Mono, monospace', fontWeight:700 }}>TOOL</span>
                  <span className="mono text-xs" style={{ color:'var(--text-2)' }}>{t.name}<span style={{ color:'var(--text-3)' }}>({t.q})</span></span>
                  {t.done
                    ? <span style={{ fontSize:9, color:'#4ade80', marginLeft:'auto' }}>✓ {t.result}</span>
                    : <span style={{ fontSize:9, color:'var(--text-3)', marginLeft:'auto' }}>running…</span>
                  }
                </div>
              ))}
            </div>
          )}
          {/* Stream text */}
          {streamText && (
            <div className="px-4 py-3 text-xs leading-relaxed" style={{ color:'var(--text-2)', background:'var(--bg-primary)', whiteSpace:'pre-wrap', maxHeight:200, overflowY:'auto' }}>
              {streamText}
              <span style={{ display:'inline-block', width:6, height:12, background: p.color, marginLeft:2, borderRadius:1, animation:'pulse .7s infinite', verticalAlign:'middle' }}/>
            </div>
          )}
        </div>
      )}

      {/* Test history */}
      {history.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="text-xs font-semibold flex items-center gap-1.5 shrink-0" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
            <History size={9}/> Test Runs ({history.length})
          </div>
          {history.map((run, idx) => (
            <TestRunCard key={run.id} run={run} idx={idx} agent={agent} isFree={isFree}/>
          ))}
        </div>
      )}

      {history.length === 0 && !isActive && (
        <div className="flex-1 flex items-center justify-center" style={{ minHeight:120 }}>
          <div className="text-center">
            <Play size={28} style={{ color:'var(--border)', margin:'0 auto 8px' }}/>
            <div style={{ fontSize:12, color:'var(--text-3)' }}>No test runs yet — enter a prompt above</div>
          </div>
        </div>
      )}
    </div>
  );
}

function TestRunCard({ run, idx, agent, isFree }) {
  const [expanded, setExpanded] = useState(idx === 0);
  const p = PROVIDERS[agent.provider];
  return (
    <div className="rounded-xl overflow-hidden shrink-0" style={{ border:'1px solid var(--border)' }}>
      {/* Card header */}
      <button onClick={() => setExpanded(x => !x)} style={{ width:'100%', cursor:'pointer', background:'none', border:'none', padding:0 }}>
        <div className="flex items-center gap-3 px-4 py-3" style={{ background:'var(--bg-tertiary)' }}>
          <span className="mono" style={{ fontSize:9, padding:'1px 7px', borderRadius:4, background:`${p.color}18`, color: p.color, fontWeight:700 }}>RUN #{idx + 1}</span>
          <span className="text-xs truncate flex-1 text-left" style={{ color:'var(--text-2)' }}>{run.prompt}</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="mono" style={{ fontSize:10, color:'var(--text-3)' }}>{fmtMs(run.latencyMs)}</span>
            <span className="mono font-bold" style={{ fontSize:10, color: isFree ? '#34d399' : '#c084fc' }}>
              {isFree ? '$0.0000' : `$${run.totalCost.toFixed(5)}`}
            </span>
            <span style={{ fontSize:9, color:'var(--text-3)' }}>{run.ts}</span>
            {expanded ? <ChevronDown size={12} style={{ color:'var(--text-3)' }}/> : <ChevronRight size={12} style={{ color:'var(--text-3)' }}/>}
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{ background:'var(--bg-primary)' }}>
          {/* Tool calls if any */}
          {run.tools.length > 0 && (
            <div className="px-4 py-2 flex flex-col gap-1" style={{ borderBottom:'1px solid var(--border)', background:'rgba(251,191,36,.03)' }}>
              {run.tools.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span style={{ fontSize:9, padding:'0 5px', borderRadius:3, background:'rgba(251,191,36,.12)', color:'#fbbf24', fontFamily:'Space Mono, monospace', fontWeight:700 }}>TOOL</span>
                  <span className="mono" style={{ color:'var(--text-2)' }}>{t.name}</span>
                  <span style={{ color:'var(--text-3)' }}>({t.q})</span>
                  <span style={{ color:'#4ade80', marginLeft:'auto', fontSize:9 }}>✓ {t.result}</span>
                </div>
              ))}
            </div>
          )}
          {/* Response text */}
          <div className="px-4 py-3 text-xs leading-relaxed" style={{ color:'var(--text-2)', whiteSpace:'pre-wrap', maxHeight:260, overflowY:'auto', borderBottom:'1px solid var(--border)' }}>
            {run.response}
          </div>
          {/* Token + cost breakdown */}
          <div className="px-4 py-3">
            <div className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px' }}>
              <DollarSign size={9}/> Token Breakdown
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                {[
                  { l:'Input Tokens',  v: fmtTok(run.inTokens),  c:'#60a5fa', cost: run.inCost  },
                  { l:'Output Tokens', v: fmtTok(run.outTokens), c:'#c084fc', cost: run.outCost },
                  { l:'Total Tokens',  v: fmtTok(run.inTokens + run.outTokens), c:'var(--text)', cost: run.totalCost },
                ].map(r => (
                  <div key={r.l} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div style={{ width:7, height:7, borderRadius:1, background: r.c, flexShrink:0 }}/>
                      <span style={{ color:'var(--text-3)' }}>{r.l}</span>
                    </div>
                    <span className="mono font-bold" style={{ color: r.c }}>{r.v}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                {[
                  { l:'Input Cost',  v: isFree ? '$0.0000' : `$${run.inCost.toFixed(5)}`,   c:'#60a5fa' },
                  { l:'Output Cost', v: isFree ? '$0.0000' : `$${run.outCost.toFixed(5)}`,  c:'#c084fc' },
                  { l:'Total Cost',  v: isFree ? '$0.0000' : `$${run.totalCost.toFixed(5)}`,c:'#34d399' },
                ].map(r => (
                  <div key={r.l} className="flex items-center justify-between text-xs">
                    <span style={{ color:'var(--text-3)' }}>{r.l}</span>
                    <span className="mono font-bold" style={{ color: r.c }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 pt-2" style={{ borderTop:'1px solid var(--border)', fontSize:10, color:'var(--text-3)' }}>
              <span>Latency: <span className="mono font-bold" style={{ color:'#fbbf24' }}>{fmtMs(run.latencyMs)}</span></span>
              <span>Model: <span className="mono" style={{ color:'var(--text-2)' }}>{agent.model}</span></span>
              <span style={{ marginLeft:'auto' }}>{run.ts}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Conversation History ────────────────────────────────────────────────
function HistoryTab({ agent }) {
  const [selConv, setSelConv] = useState(null);
  const convs = makeConvs(agent.id);
  return (
    <div className="flex gap-4 h-full overflow-hidden">
      {/* Conversation list */}
      <div className="flex flex-col gap-2 shrink-0" style={{ width: 220 }}>
        <div className="text-xs font-semibold mb-1" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
          {convs.length} conversations
        </div>
        {convs.map(cv => (
          <button key={cv.id} onClick={() => setSelConv(cv)}
            className="text-left rounded-xl px-3 py-2.5 flex flex-col gap-1 transition-all"
            style={{ background: selConv?.id === cv.id ? `${PROVIDERS[agent.provider].color}15` : 'var(--bg-tertiary)',
              border: `1px solid ${selConv?.id === cv.id ? PROVIDERS[agent.provider].color + '40' : 'var(--border)'}`,
              cursor:'pointer' }}>
            <div className="text-xs font-semibold truncate" style={{ color:'var(--text)' }}>{cv.topic}</div>
            <div className="flex items-center gap-2 text-xs" style={{ color:'var(--text-3)' }}>
              <span>{cv.turns.length} turns</span>
              <span>·</span>
              <span>{cv.time}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span style={{ color:'var(--text-3)' }}>{cv.tokens} tok</span>
              <span className="mono" style={{ color:'#34d399' }}>{fmtCost(cv.cost)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Conversation detail */}
      <div className="flex-1 overflow-y-auto">
        {!selConv ? (
          <div className="flex items-center justify-center h-full" style={{ color:'var(--text-3)', fontSize:12 }}>
            Select a conversation to view
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {selConv.turns.map((turn, i) => {
              if (turn.role === 'tool') {
                return (
                  <div key={i} className="rounded-lg px-3 py-2 mx-2"
                    style={{ background:'rgba(192,132,252,.06)', border:'1px solid rgba(192,132,252,.2)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Zap size={10} style={{ color:'#c084fc' }}/>
                      <span style={{ fontSize:9, fontWeight:700, color:'#c084fc' }}>TOOL CALL</span>
                      <span className="mono text-xs font-bold" style={{ color:'#a78bfa' }}>{turn.name}</span>
                    </div>
                    <div style={{ fontSize:9, color:'var(--text-3)', fontFamily:'monospace' }}>→ {turn.input}</div>
                    <div style={{ fontSize:9, color:'var(--text-2)', fontFamily:'monospace', marginTop:2 }}>← {turn.output}</div>
                  </div>
                );
              }
              const isUser = turn.role === 'user';
              return (
                <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'} px-2`}>
                  <div style={{ maxWidth:'82%', background: isUser ? 'rgba(96,165,250,.12)' : 'var(--bg-tertiary)',
                    border: `1px solid ${isUser ? 'rgba(96,165,250,.25)' : 'var(--border)'}`,
                    borderRadius: 12, padding:'10px 12px' }}>
                    <div style={{ fontSize:9, fontWeight:700, color: isUser ? '#60a5fa' : PROVIDERS[agent.provider].color, marginBottom:4, textTransform:'uppercase' }}>
                      {isUser ? '👤 User' : `🤖 ${agent.name}`}
                    </div>
                    <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color:'var(--text)' }}>{turn.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Debug Console ───────────────────────────────────────────────────────
function DebugTab({ agent }) {
  const [live, setLive]     = useState(false);
  const [events, setEvents] = useState(DEBUG_EVENTS_BASE.slice(0, 8));
  const [filter, setFilter] = useState('ALL');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!live) return;
    const idx = { current: events.length % DEBUG_EVENTS_BASE.length };
    const id = setInterval(() => {
      const ev = { ...DEBUG_EVENTS_BASE[idx.current % DEBUG_EVENTS_BASE.length], ts: new Date().toLocaleTimeString('en-US', { hour12:false }) };
      setEvents(prev => [...prev.slice(-49), ev]);
      idx.current++;
    }, 1400);
    return () => clearInterval(id);
  }, [live]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [events]);

  const filtered = filter === 'ALL' ? events : events.filter(e => e.level === filter || e.type === filter);
  const evTypes = ['ALL','INFO','DEBUG','WARN','ERROR'];

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => setLive(l => !l)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: live ? 'rgba(34,197,94,.15)' : 'var(--bg-tertiary)', border: `1px solid ${live ? 'rgba(34,197,94,.3)' : 'var(--border)'}`, color: live ? '#4ade80' : 'var(--text-3)', cursor:'pointer' }}>
          {live ? <><Pause size={10}/> Live</> : <><Play size={10}/> Start Live</>}
        </button>
        <div className="flex items-center gap-1">
          {evTypes.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="px-2 py-1 rounded text-xs font-bold transition-all"
              style={{ background: filter===f ? (LEVEL_COLOR[f]||'var(--blue)') + '20' : 'transparent',
                color: filter===f ? (LEVEL_COLOR[f]||'var(--text)') : 'var(--text-3)',
                border: `1px solid ${filter===f ? (LEVEL_COLOR[f]||'var(--blue)') + '40' : 'transparent'}`,
                cursor:'pointer', fontSize:9 }}>
              {f}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs" style={{ color:'var(--text-3)' }}>{filtered.length} events</span>
        <button onClick={() => setEvents(DEBUG_EVENTS_BASE.slice(0,8))}
          className="text-xs px-2 py-1 rounded" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', color:'var(--text-3)', cursor:'pointer' }}>
          Clear
        </button>
      </div>

      {/* Event stream */}
      <div className="flex-1 overflow-y-auto rounded-xl" style={{ background:'var(--bg-primary)', border:'1px solid var(--border)', fontFamily:'Space Mono, monospace' }}>
        {filtered.map((ev, i) => {
          const ec = EVENT_TYPE_COLOR[ev.type] || '#94a3b8';
          const lc = LEVEL_COLOR[ev.level] || '#64748b';
          return (
            <div key={i} className="flex items-start gap-3 px-3 py-2 hover:bg-white/[0.02]"
              style={{ borderBottom:'1px solid rgba(255,255,255,.03)' }}>
              <span style={{ fontSize:9, color:'var(--text-3)', whiteSpace:'nowrap', marginTop:1 }}>{ev.ts || `T+${(i*1.4).toFixed(1)}s`}</span>
              <span style={{ fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:3, background:`${lc}18`, color:lc, flexShrink:0, marginTop:1 }}>{ev.level}</span>
              <span style={{ fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:3, background:`${ec}18`, color:ec, flexShrink:0, marginTop:1 }}>{ev.type}</span>
              <div className="flex-1 min-w-0">
                <div style={{ fontSize:10, color:'var(--text)', marginBottom:1 }}>{ev.msg}</div>
                <div style={{ fontSize:9, color:'var(--text-3)', wordBreak:'break-all' }}>{ev.detail}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
    </div>
  );
}

// ─── Tab: Configuration ───────────────────────────────────────────────────────
function ConfigTab({ agent }) {
  const p = PROVIDERS[agent.provider];
  const config = {
    provider:         agent.provider,
    model:            agent.model,
    assistant_id:     agent.assistantId,
    environment:      agent.env,
    version:          agent.version,
    temperature:      agent.temperature,
    max_tokens:       agent.maxTokens,
    context_window:   agent.contextWindow,
    tools:            agent.tools,
    knowledge_bases:  agent.knowledgeBases,
    sla_target_pct:   agent.slaTarget,
    compliance_tags:  agent.compliance,
    cost_center:      agent.costCenter,
    owner:            agent.owner,
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-xs" style={{ color:'var(--text-3)' }}>
        <Lock size={10}/>
        <span>Configuration is version-controlled. Changes deploy via GitOps pipeline.</span>
        <span className="ml-auto px-2 py-0.5 rounded font-bold" style={{ background:`${p.color}15`, color:p.color, fontSize:9 }}>v{agent.version}</span>
      </div>
      {/* Config JSON */}
      <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)' }}>
          <span className="text-xs font-semibold" style={{ color:'var(--text-3)' }}>agent-config.json</span>
          <button onClick={() => navigator.clipboard?.writeText(JSON.stringify(config, null, 2))}
            className="flex items-center gap-1 text-xs" style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer' }}>
            <Copy size={10}/> Copy
          </button>
        </div>
        <pre style={{ padding:'16px', fontSize:10, color:'var(--text-2)', background:'var(--bg-primary)', overflow:'auto', maxHeight:400, fontFamily:'Space Mono, monospace', lineHeight:1.8 }}>
          {JSON.stringify(config, null, 2)}
        </pre>
      </div>
      {/* Config fields */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { l:'Temperature',   v: agent.temperature, color:'#fbbf24' },
          { l:'Max Tokens',    v: agent.maxTokens.toLocaleString(), color:'#60a5fa' },
          { l:'Context Window',v:`${(agent.contextWindow/1000).toFixed(0)}K tokens`, color:'#c084fc' },
          { l:'SLA Target',    v:`${agent.slaTarget}%`, color:'#34d399' },
        ].map(f => (
          <div key={f.l} className="rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:9, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:3 }}>{f.l}</div>
            <div className="mono font-bold text-sm" style={{ color:f.color }}>{f.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: Audit Log ───────────────────────────────────────────────────────────
function AuditTab({ agent }) {
  const logs = makeAuditLog(agent);
  const actionColor = { deploy:'#60a5fa', config_change:'#fbbf24', auto_scale:'#c084fc', kb_update:'#34d399', access_grant:'#f97316', create:'#22c55e' };
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>{logs.length} events in audit log</div>
      {logs.map((log, i) => {
        const c = actionColor[log.action] || '#94a3b8';
        return (
          <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-xl"
            style={{ background:'var(--bg-tertiary)', border:`1px solid ${c}15` }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:c, flexShrink:0, marginTop:5 }}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span style={{ fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:3, background:`${c}18`, color:c }}>{log.action.replace('_',' ').toUpperCase()}</span>
                <span style={{ fontSize:9, color:'var(--text-3)' }}>{log.user}</span>
              </div>
              <p className="text-xs" style={{ color:'var(--text-2)' }}>{log.detail}</p>
            </div>
            <span style={{ fontSize:9, color:'var(--text-3)', flexShrink:0, whiteSpace:'nowrap' }}>{log.ts}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────
const DETAIL_TABS = ['Overview','Test','History','Debug','Config','Audit'];

function AgentDetailPanel({ agent }) {
  const [tab, setTab] = useState('Overview');
  const p = PROVIDERS[agent.provider];
  const s = STATUS_CFG[agent.status];

  return (
    <div className="flex flex-col h-full">
      {/* Agent header */}
      <div className="px-5 py-4 shrink-0" style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)', borderLeft:`3px solid ${p.color}` }}>
        <div className="flex items-start gap-4 mb-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0"
            style={{ background: p.bg, border: `1px solid ${p.color}30` }}>
            {p.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="font-bold text-base">{agent.name}</h2>
              <StatusDot status={agent.status}/>
              <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:4, background: ENV_COLOR[agent.env] + '18', color: ENV_COLOR[agent.env], border: `1px solid ${ENV_COLOR[agent.env]}30` }}>{agent.env.toUpperCase()}</span>
            </div>
            <div className="flex items-center gap-3 text-xs flex-wrap" style={{ color:'var(--text-3)' }}>
              <ProviderBadge provider={agent.provider} size="xs"/>
              <span className="mono">{agent.model}</span>
              <span className="mono" style={{ opacity:0.6 }}>{agent.assistantId.slice(0, 26)}…</span>
            </div>
          </div>
          <HealthRing score={agent.healthScore} size={52}/>
        </div>

        {/* Quick stats row */}
        <div className="flex items-center gap-3 flex-wrap mb-3">
          {[
            { l:'Today', v: agent.requestsToday.toLocaleString()+' req', c:'#60a5fa' },
            { l:'Error', v: agent.errRate+'%', c: agent.errRate > 5 ? '#f87171' : '#34d399' },
            { l:'P95',   v: fmtMs(agent.p95LatencyMs), c:'#fbbf24' },
            { l:'$/mo',  v: fmtCost(agent.costMonth), c:'#c084fc' },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
              style={{ background:`${s.c}12`, border:`1px solid ${s.c}25` }}>
              <span style={{ fontSize:9, color:'var(--text-3)' }}>{s.l}</span>
              <span className="mono font-bold" style={{ fontSize:10, color:s.c }}>{s.v}</span>
            </div>
          ))}
          <span className="ml-auto text-xs" style={{ color:'var(--text-3)' }}>Last active {agent.lastActive}</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-0" style={{ marginBottom: -1 }}>
          {DETAIL_TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className="px-3 py-2 text-xs font-semibold transition-colors"
              style={{ background:'none', border:'none', cursor:'pointer',
                borderBottom: tab===t ? `2px solid ${p.color}` : '2px solid transparent',
                color: tab===t ? p.color : 'var(--text-3)' }}>
              {t === 'Debug' ? '⚡ Debug' : t}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-5">
        {tab === 'Overview' && <OverviewTab agent={agent}/>}
        {tab === 'Test'     && <TestTab     agent={agent}/>}
        {tab === 'History'  && <HistoryTab  agent={agent}/>}
        {tab === 'Debug'    && <DebugTab    agent={agent}/>}
        {tab === 'Config'   && <ConfigTab   agent={agent}/>}
        {tab === 'Audit'    && <AuditTab    agent={agent}/>}
      </div>
    </div>
  );
}

// ─── Agent list item ──────────────────────────────────────────────────────────
function AgentListItem({ agent, selected, onClick }) {
  const p = PROVIDERS[agent.provider];
  const s = STATUS_CFG[agent.status];
  return (
    <button onClick={onClick} className="w-full text-left px-4 py-3 flex gap-3 items-start transition-all"
      style={{ background: selected ? `${p.color}10` : 'transparent',
        borderLeft: `3px solid ${selected ? p.color : 'transparent'}`,
        borderBottom: '1px solid var(--border)', cursor:'pointer' }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0 mt-0.5"
        style={{ background: p.bg, border:`1px solid ${p.color}25` }}>{p.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-xs font-bold truncate">{agent.name}</span>
          <StatusDot status={agent.status}/>
        </div>
        <div className="flex items-center gap-1.5 mb-1" style={{ color:'var(--text-3)' }}>
          <span style={{ fontSize:9 }}>{p.icon}</span>
          <span style={{ fontSize:9 }}>{agent.model}</span>
          <span style={{ fontSize:9, color: ENV_COLOR[agent.env] }}>· {agent.env}</span>
        </div>
        <div className="flex items-center gap-2">
          <HealthRing score={agent.healthScore} size={24}/>
          <span style={{ fontSize:9, color:'var(--text-3)' }}>{agent.requestsToday.toLocaleString()} req today</span>
          <span style={{ fontSize:9, color: agent.errRate > 5 ? '#f87171' : '#34d399', marginLeft:'auto', fontWeight:700 }}>{agent.errRate}% err</span>
        </div>
      </div>
    </button>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function AgentInventoryView() {
  const [selected,   setSelected]   = useState(INVENTORY[0].id);
  const [search,     setSearch]     = useState('');
  const [filterProv, setFilterProv] = useState('ALL');
  const [filterEnv,  setFilterEnv]  = useState('ALL');
  const [filterStat, setFilterStat] = useState('ALL');

  const selAgent = INVENTORY.find(a => a.id === selected) || INVENTORY[0];

  const filtered = INVENTORY.filter(a => {
    if (filterProv !== 'ALL' && a.provider !== filterProv)  return false;
    if (filterEnv  !== 'ALL' && a.env      !== filterEnv)   return false;
    if (filterStat !== 'ALL' && a.status   !== filterStat)  return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) &&
                  !a.model.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const activeCount   = INVENTORY.filter(a => a.status === 'active').length;
  const degradedCount = INVENTORY.filter(a => a.status === 'degraded' || a.status === 'error').length;
  const totalCostMo   = INVENTORY.reduce((s, a) => s + a.costMonth, 0);
  const totalReqToday = INVENTORY.reduce((s, a) => s + a.requestsToday, 0);

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)' }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background:'linear-gradient(135deg,#10a37f,#4285f4)', boxShadow:'0 4px 14px rgba(16,163,127,.4)' }}>
          <Bot size={18} className="text-white"/>
        </div>
        <div>
          <h1 className="text-base font-bold">Agent Inventory</h1>
          <p className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>
            Cross-provider agent registry · traces · debug console · conversation history · audit log
          </p>
        </div>
        {/* Summary chips */}
        <div className="flex items-center gap-2 flex-wrap ml-2">
          {[
            { l:'Agents',    v: INVENTORY.length,            c:'#60a5fa' },
            { l:'Active',    v: activeCount,                  c:'#34d399' },
            { l:'Degraded',  v: degradedCount,                c: degradedCount > 0 ? '#f87171' : '#34d399' },
            { l:'Req Today', v: totalReqToday.toLocaleString(),c:'#c084fc' },
            { l:'Cost /mo',  v: `$${totalCostMo.toFixed(0)}`, c:'#fbbf24' },
          ].map(c => (
            <div key={c.l} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
              style={{ background:`${c.c}12`, border:`1px solid ${c.c}25` }}>
              <span style={{ color:'var(--text-3)' }}>{c.l}</span>
              <span className="mono font-bold" style={{ color:c.c }}>{c.v}</span>
            </div>
          ))}
        </div>
        {/* Provider filter pills */}
        <div className="ml-auto flex items-center gap-1 flex-wrap">
          {['ALL', ...Object.keys(PROVIDERS)].map(prov => {
            const p = prov === 'ALL' ? null : PROVIDERS[prov];
            const active = filterProv === prov;
            return (
              <button key={prov} onClick={() => setFilterProv(prov)}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                style={{ background: active ? (p?.bg || 'rgba(96,165,250,.12)') : 'var(--bg-primary)',
                  border:`1px solid ${active ? (p?.color || '#60a5fa') + '50' : 'var(--border)'}`,
                  color: active ? (p?.color || '#60a5fa') : 'var(--text-3)', cursor:'pointer' }}>
                {prov === 'ALL' ? 'All' : p.icon + ' ' + p.label.split(' ')[0]}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — agent list */}
        <div className="flex flex-col shrink-0" style={{ width:300, borderRight:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
          {/* Search + filters */}
          <div className="p-3 flex flex-col gap-2 shrink-0" style={{ borderBottom:'1px solid var(--border)' }}>
            <div className="relative">
              <Search size={11} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-3)' }}/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agents…"
                style={{ width:'100%', padding:'6px 8px 6px 28px', borderRadius:8, fontSize:11,
                  background:'var(--bg-primary)', border:'1px solid var(--border)', color:'var(--text)', outline:'none' }}/>
            </div>
            <div className="flex gap-2">
              <select value={filterEnv} onChange={e => setFilterEnv(e.target.value)}
                style={{ flex:1, padding:'4px 7px', borderRadius:7, fontSize:10, background:'var(--bg-primary)', border:'1px solid var(--border)', color:'var(--text)', outline:'none' }}>
                <option value="ALL">All Envs</option>
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </select>
              <select value={filterStat} onChange={e => setFilterStat(e.target.value)}
                style={{ flex:1, padding:'4px 7px', borderRadius:7, fontSize:10, background:'var(--bg-primary)', border:'1px solid var(--border)', color:'var(--text)', outline:'none' }}>
                <option value="ALL">All Status</option>
                <option value="active">Active</option>
                <option value="degraded">Degraded</option>
                <option value="error">Error</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs" style={{ color:'var(--text-3)' }}>No agents match filters</div>
            ) : filtered.map(agent => (
              <AgentListItem key={agent.id} agent={agent}
                selected={selected === agent.id}
                onClick={() => setSelected(agent.id)}/>
            ))}
          </div>

          {/* Provider summary */}
          <div className="p-3 shrink-0" style={{ borderTop:'1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>By Provider</div>
            {Object.entries(PROVIDERS).map(([key, p]) => {
              const count = INVENTORY.filter(a => a.provider === key).length;
              if (!count) return null;
              return (
                <div key={key} className="flex items-center gap-2 mb-1.5 text-xs">
                  <span>{p.icon}</span>
                  <span style={{ color:'var(--text-2)', flex:1 }}>{p.label}</span>
                  <div className="flex-1 h-1 rounded-full overflow-hidden mx-1" style={{ background:'var(--bg-primary)' }}>
                    <div style={{ width:`${(count/INVENTORY.length)*100}%`, height:'100%', background:p.color, borderRadius:99 }}/>
                  </div>
                  <span className="mono font-bold" style={{ color:p.color, width:16, textAlign:'right' }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel — detail */}
        <div className="flex-1 overflow-hidden">
          {selAgent ? <AgentDetailPanel agent={selAgent}/> : (
            <div className="flex items-center justify-center h-full" style={{ color:'var(--text-3)' }}>
              Select an agent
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
